import type { mfaClient } from "../api/mfaAuthClient";

export type MfaFactor = {
  id: string;
  factor_type: string;
  status: string;
  friendly_name?: string | null;
};

type MfaApi = Pick<typeof mfaClient.auth.mfa, "listFactors" | "unenroll" | "enroll">;

export type EnrollmentPreparation =
  | { kind: "reuse-verified"; factorId: string }
  | { kind: "enrolled"; factorId: string; qrCode: string; secret: string }
  | { kind: "unauthorized" }
  | { kind: "error"; error: unknown };

export function isVerifiedTotpFactor(factor: MfaFactor): boolean {
  return factor.factor_type === "totp" && factor.status === "verified";
}

export function isOrphanedTotpFactor(factor: MfaFactor): boolean {
  return factor.factor_type === "totp" && factor.status === "unverified" && !(factor.friendly_name ?? "").trim();
}

export function findVerifiedTotpFactor(factors: readonly MfaFactor[]): MfaFactor | null {
  return factors.find(isVerifiedTotpFactor) ?? null;
}

export function findOrphanedTotpFactors(factors: readonly MfaFactor[]): MfaFactor[] {
  return factors.filter(isOrphanedTotpFactor);
}

function isUnauthorized(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && error.status === 401;
}

function resultForError(error: unknown): EnrollmentPreparation {
  return isUnauthorized(error) ? { kind: "unauthorized" } : { kind: "error", error };
}

/**
 * Resolve the safe enrollment sequence: reuse a verified factor first, otherwise remove only
 * unnamed unverified TOTP factors and create a new one. Keeping the ordering here prevents a
 * retry from deleting the factor that an interrupted audit already verified.
 */
export async function prepareMfaEnrollment(api: MfaApi): Promise<EnrollmentPreparation> {
  const { data: factors, error: factorsError } = await api.listFactors();
  if (factorsError) return resultForError(factorsError);

  const allFactors = (factors?.all ?? []) as MfaFactor[];
  const verifiedFactor = findVerifiedTotpFactor(allFactors);
  if (verifiedFactor) return { kind: "reuse-verified", factorId: verifiedFactor.id };

  for (const factor of findOrphanedTotpFactors(allFactors)) {
    const { error } = await api.unenroll({ factorId: factor.id });
    if (error) return resultForError(error);
  }

  const { data, error: enrollError } = await api.enroll({ factorType: "totp" });
  if (enrollError) return resultForError(enrollError);
  if (!data) return { kind: "error", error: new Error("Matrícula MFA sem dados do fator.") };

  return {
    kind: "enrolled",
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
}
